/**
 * Stripe client wrapper.
 *
 * As of the security migration, all Stripe SDK calls run on the backend
 * (`mentis_server` → `/api/v1/stripe/*`). The Stripe secret key NEVER
 * touches the browser anymore. This module is now just a typed HTTP client
 * that forwards a Firebase ID token and returns the same shape the previous
 * client-side implementation did, so callers don't need to change.
 */

import { auth } from '../firebase/config';
import { API_BASE_URL } from '../config/api';

// ----------------------------- helpers --------------------------------------

const STRIPE_BASE = `${API_BASE_URL}/api/v1/stripe`;

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  // Force refresh so we always have a valid token (Firebase tokens are 1h).
  return user.getIdToken(true);
}

async function postJson<TResp>(path: string, body: unknown): Promise<TResp> {
  const token = await getAuthToken();
  const res = await fetch(`${STRIPE_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  // Backend returns structured `{ success, error, ... }` payloads even for
  // expected failures (Stripe errors). Surface those directly. Only treat
  // 5xx / network failures as thrown errors.
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Stripe API ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as TResp;
}

// ----------------------------- public types ---------------------------------

export interface StripeCustomerData {
  customerId: string;
  email: string;
  name?: string;
}

// ----------------------------- exports --------------------------------------

/**
 * Find or create a Stripe customer by email. Calls the backend so the
 * Stripe secret key stays server-side.
 */
export const findOrCreateStripeCustomer = async (params: {
  email: string;
  name?: string;
  firebaseUid: string; // kept for API compatibility; backend resolves uid from token
  phone?: string;
  metadata?: Record<string, string>;
}): Promise<{
  success: boolean;
  customer?: StripeCustomerData;
  existingCustomer?: boolean;
  error?: string;
}> => {
  try {
    const data = await postJson<{
      success: boolean;
      existing_customer: boolean;
      customer_id?: string;
      email?: string;
      name?: string;
      error?: string;
    }>('/customers', {
      email: params.email,
      name: params.name,
      phone: params.phone,
      metadata: params.metadata,
    });

    if (!data.success || !data.customer_id) {
      return { success: false, error: data.error || 'Failed to upsert Stripe customer' };
    }
    return {
      success: true,
      existingCustomer: data.existing_customer,
      customer: {
        customerId: data.customer_id,
        email: data.email || params.email,
        name: data.name || params.name,
      },
    };
  } catch (err: any) {
    console.error('findOrCreateStripeCustomer failed:', err);
    return { success: false, error: err?.message || 'Network error' };
  }
};

/**
 * Cancel a Stripe subscription, optionally refunding the latest payment.
 * The 3-day money-back window is decided by the caller — this just forwards
 * the `refund` flag to the backend.
 */
export const cancelStripeSubscription = async (params: {
  subscriptionId?: string;
  customerId?: string;
  refund?: boolean;
}): Promise<{
  success: boolean;
  canceled?: boolean;
  refunded?: boolean;
  refundAmount?: number;
  refundCurrency?: string;
  refundId?: string;
  error?: string;
}> => {
  try {
    const data = await postJson<{
      success: boolean;
      canceled: boolean;
      refunded: boolean;
      refund_amount?: number;
      refund_currency?: string;
      refund_id?: string;
      error?: string;
    }>('/cancel-subscription', {
      subscription_id: params.subscriptionId,
      customer_id: params.customerId,
      refund: !!params.refund,
    });

    if (!data.success) {
      return { success: false, error: data.error || 'Cancellation failed' };
    }
    return {
      success: true,
      canceled: data.canceled,
      refunded: data.refunded,
      refundAmount: data.refund_amount,
      refundCurrency: data.refund_currency,
      refundId: data.refund_id,
    };
  } catch (err: any) {
    console.error('cancelStripeSubscription failed:', err);
    return { success: false, error: err?.message || 'Network error' };
  }
};

const StripeService = {
  findOrCreateStripeCustomer,
  cancelStripeSubscription,
};

export default StripeService;
